import { ThemeOptions } from "@mui/material";
import { generateDefaultGJTypography, MuiChip } from "./util/theme-generator-util";

declare module "@mui/material/styles" {
  interface Components {
    [key: string]: any;
  }
}

export const bunkHotelFontFamilyRegular = "Roboto";

export const bunkHotelThemeConfig: ThemeOptions = {
  components: {
    MuiChip,
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            backgroundColor: theme.palette.common.white,
            "&:last-child": {
              padding: 16
            }
          };
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.palette.grey[600]}`
          };
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          margin: 0,
          borderRadius: Number(theme.shape.borderRadius) * 2.5,
          boxShadow: "0px 4px 16px rgba(197, 185, 172, 0.06)"
        })
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            backgroundColor: `${theme.palette.common.white} !important`,
            borderWidth: `1px !important`,
            span: { color: `${theme.palette.common.black} !important` },
            "&.Mui-disabled": {
              span: { color: `${theme.palette.grey[300]} !important` }
            }
          };
        },
        label: ({ theme }) => ({
          color: theme.palette.common.black
        })
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            borderTopRightRadius: theme.shape.borderRadius,
            borderTopLeftRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.palette.common.black}`,
            svg: {
              color: theme.palette.grey[600]
            }
          };
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: `
      .FakeInputBox {
          border: 1px solid #FFFFFF;
           > div > div > svg {
            stroke: #FFFFFF !important;
            width: 24px;
            height: 24px;
          }
      }
      `
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            "&::after": {
              borderBottom: "none"
            },
            borderTopRightRadius: theme.shape.borderRadius,
            borderTopLeftRadius: theme.shape.borderRadius,
            backgroundColor: "rgba(0, 0, 0, 0.04)"
          };
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: any) => {
          return {
            border: `1px solid ${theme.palette.common.black}`
          };
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            backgroundColor: `${theme.palette.grey[600]}`,
            border: "none",
            height: 1
          };
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => {
          return {
            backgroundColor: theme.palette.common.black,
            height: 1
          };
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 36
        }
      },
      variants: [
        {
          props: { variant: "primary" },
          style: ({ theme }) => ({
            background: theme.palette.primary.main,
            "&:hover": { backgroundColor: theme.palette.primary.main }
          })
        },
        {
          props: { variant: "secondary" },
          style: ({ theme }) => ({
            borderColor: theme.palette.common.black,
            border: "1px solid",
            "&:hover": { backgroundColor: theme.palette.common.white }
          })
        }
      ]
    },
    MuiButtonBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiSvgIcon-root": {
            backgroundColor: `${theme.palette.common.white}`,
            borderColor: `${theme.palette.primary.main}`,
            borderWidth: 1,
            svg: {
              color: `${theme.palette.primary.main}`
            }
          },
          "& .MuiPaper-root": {
            background: theme.palette.common.white,
            border: `1px solid ${theme.palette.common.black}`
          }
        })
      }
    },
    MuiDateRangePickerDay: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          "& .Mui-disabled": {
            color: `${theme.palette.grey[600]} !important`
          }
        })
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme }) => ({
          span: {
            svg: {
              color: `${theme.palette.common.black} !important`,
              backgroundColor: "transparent !important"
            }
          }
        })
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: "none",
          backgroundColor: `${theme.palette.common.white} !important`,
          button: {
            color: `${theme.palette.common.black} !important`,
            svg: {
              color: `${theme.palette.common.black} !important`
            },
            span: {
              svg: {
                color: `${theme.palette.common.black} !important`
              }
            }
          },
          div: {
            button: {
              img: {
                height: "44px"
              }
            }
          }
        })
      }
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: ({ theme }) => ({
          svg: {
            color: `${theme.palette.common.black} !important`,
            path: {
              stroke: `${theme.palette.common.black} !important`
            }
          },
          a: {
            span: {
              p: {
                fontWeight: "300",
                fontSize: "12px !important",
                lineHeight: "20px",
                letterSpacing: ".5px",
                textTransform: "uppercase"
              }
            }
          }
        })
      }
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            fontSize: 16,
            textTransform: "capitalize",
            color: `${theme.palette.common.black} !important`,
            "&.Mui-selected": {
              color: `${theme.palette.common.black} !important`,
              fontWeight: 700
            },
            [theme.breakpoints.up("md")]: {
              paddingInline: theme.spacing(2.5)
            }
          };
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          lineHeight: "1em",
          "&.Mui-focused": {
            color: "white"
          }
        }
      }
    }
  },
  palette: {
    primary: { main: "#DD3927" },
    secondary: {
      main: "#FFFFFF"
    },
    background: {
      paper: "#000000",
      default: "#000000"
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#FFFFFF"
    },
    common: {
      black: "#FFFFFF",
      white: "#000000"
    },
    contrastThreshold: 5
  },
  shape: {
    borderRadius: 0
  },
  typography: generateDefaultGJTypography(bunkHotelFontFamilyRegular)
};
